import { platformApiClient } from '@/services/platform-api-client';

/**
 * Provider-to-provider storage migrations (OQ-R2 / D-124).
 *
 * Deliberately a SEPARATE client from `platform-storage.api.ts`, which serves
 * the LOCAL PATH migrations at `/platform/storage/migrations`. OQ-AME-1 exists
 * precisely to keep directory relocation and provider migration apart; sharing
 * one client here would reintroduce the confusion that separation removed.
 *
 * Reads are GETs. There are exactly FIVE mutating calls:
 *
 *   1. `planProviderMigration`     — creates a dry-run PLAN and copies nothing.
 *   2. `approveProviderMigration`  — DRY_RUN_COMPLETE -> READY. Copies nothing.
 *   3. `runProviderMigration`      — copies and verifies the planned objects.
 *                                    Changes NO ownership and deletes nothing.
 *   4. `flipProviderMigrationOwnership`
 *                                  — moves `storage_assets.provider_id` source
 *                                    -> target for verified items only.
 *                                    Deletes nothing.
 *   5. `finalizeProviderMigration` — VERIFYING -> COMPLETED. Copies, moves and
 *                                    deletes nothing.
 *
 * plus two REHEARSALS that are POSTs but write nothing —
 * `previewProviderMigrationOwnership` and `previewFinalizeProviderMigration`.
 * Both run the real checks with `dryRun: true` and are gated on
 * `platform:system:view`.
 *
 * ── WHY THE OWNERSHIP FLIP IS NOW REACHABLE FROM THE BROWSER ────────────────
 * It previously was not, and omitting the wrapper was described here as the
 * enforcement. That left a workflow with a hole in the middle: the UI offered
 * Run and then Finalize, but the ownership transition sits BETWEEN them, so
 * every migration driven from this console reached Finalize with its assets
 * still on the source provider and was refused by finalization gate
 * `ASSET_NOT_ON_TARGET_PROVIDER` with a 409 the operator could not act on. The
 * step had to be performed out-of-band by an engineer for the migration to
 * complete at all.
 *
 * The enforcement is therefore relocated, not removed. What actually keeps the
 * flip safe is unchanged and lives on the server: it moves ownership only for
 * items whose copy is verified, re-downloads and re-hashes every destination
 * object first, refuses any status other than VERIFYING, writes one asset per
 * tenant-scoped transaction asserting exactly one affected row, and deletes
 * nothing. On this side it requires an explicit human confirmation and is
 * called from exactly one place — `OwnershipTransferPanel`.
 *
 * Still deliberately ABSENT: no `ownership/rollback` wrapper, and no cleanup,
 * source-deletion, purge or provider-deactivation wrapper of any kind. No
 * deletion path is reachable from the browser.
 *
 * NOTE: the `platformApiClient` response interceptor already unwraps
 * `{ data: T } -> T`, so `r.data` is the payload itself. Do not unwrap twice.
 */

export interface ProviderMigrationListItemDto {
  id: string;
  status: string;
  sourceProviderId: string;
  targetProviderId: string;
  totalObjects: number | null;
  migratedObjects: number;
  verifiedObjects: number;
  failedObjects: number;
  skippedObjects: number;
  dryRunApproved: boolean;
  createdAt: string;
}

/** The exclusion record persisted in `dry_run_report` at planning time. */
export interface ProviderMigrationExclusions {
  policy?: string;
  excludedCount?: number;
  excludedByStatus?: Record<string, number>;
  excludedAssetIds?: string[];
}

/** The planning snapshot. Shapes are optional because it is a stored JSON blob. */
export interface ProviderMigrationSnapshot {
  exclusions?: ProviderMigrationExclusions;
  /**
   * The planner's own tallies. Optional because this is a stored JSON blob:
   * `alreadyOnDestination` only appears in reports produced after the planner
   * learned to recognise byte-identical destination objects.
   */
  totals?: {
    candidateObjects?: number;
    wouldMigrate?: number;
    blocked?: number;
    alreadyOnDestination?: number;
  };
  plannedBytes?: number;
  plannedAssetCount?: number;
  largestObjectBytes?: number;
  sourceRetentionRule?: string;
  decisionOfRecord?: string;
  architectureOfRecord?: string;
  source?: { providerId: string; driverId: string };
  destination?: { providerId: string; driverId: string };
}

export interface ProviderMigrationDetailDto {
  id: string;
  status: string;
  sourceProviderId: string;
  targetProviderId: string;
  totalObjects: number | null;
  migratedObjects: number;
  verifiedObjects: number;
  failedObjects: number;
  skippedObjects: number;
  dryRunApproved: boolean;
  cursor: string | null;
  initiatedById: string;
  startedAt: string | null;
  completedAt: string | null;
  pausedAt: string | null;
  rolledBackAt: string | null;
  createdAt: string;
  updatedAt: string;
  dryRunReport: ProviderMigrationSnapshot | null;
  itemCount: number;
  /** Items whose copy is checksum-verified. Derived from the item rows. */
  itemsVerified: number;
  itemsByStatus: Record<string, number>;
  /**
   * Live, server-derived progress of the copy phase.
   *
   * Recomputed by the server from the item rows on every read — never a stored
   * counter, never a client-side estimate. The UI renders these numbers; it must
   * never advance them on a timer.
   *
   * Optional so a client can still render against a server that predates it.
   */
  progress?: ProviderMigrationProgressDto | null;
  /**
   * Where this migration's assets are owned RIGHT NOW.
   *
   * Optional because a client may be talking to a backend that predates it; the
   * workflow treats an absent value as "not proven transferred" and routes the
   * operator to the read-only ownership rehearsal, never straight to Finalize.
   */
  ownership?: ProviderMigrationOwnershipStateDto | null;
}

/**
 * The ownership position of a migration's assets, computed by the server from
 * `storage_assets.provider_id` on every read.
 *
 * Direction-agnostic: `onSource` / `onTarget` are resolved against the
 * migration's own provider ids, so the same three counters describe R2 -> S3,
 * S3 -> R2, LOCAL -> R2 and R2 -> LOCAL. No provider id or driver name is
 * hardcoded anywhere in this feature.
 */
export interface ProviderMigrationOwnershipStateDto {
  totalAssets: number;
  onTarget: number;
  onSource: number;
  /** On neither the source nor the target — never counted as transferred. */
  onOther: number;
  /** Referenced by an item but no longer resolvable. Never counted as moved. */
  missing: number;
  notActive: number;
  /**
   * Server-computed: every asset of this migration is owned by the target.
   * The client must not recompute this from the counters — one authority only.
   */
  transferred: boolean;
}

/**
 * Derived progress of the copy phase. Every field comes from the item rows or
 * the runner's own cursor; nothing here is estimated.
 */
export interface ProviderMigrationProgressDto {
  total: number;
  completed: number;
  verified: number;
  failed: number;
  skipped: number;
  pending: number;
  inProgress: number;
  /** Integer 0-100. */
  percent: number;
  /** The runner's cursor: the last storage key it actually processed. */
  currentKey: string | null;
  /** 1-based position the runner has reached. */
  currentIndex: number;
}

export interface ProviderMigrationItemDto {
  id: string;
  assetId: string;
  storageKey: string;
  fileName: string | null;
  assetClass: string | null;
  mimeType: string | null;
  fileSizeBytes: string | null;
  status: string;
  verified: boolean;
  sourceChecksum: string | null;
  targetChecksum: string | null;
  checksumsMatch: boolean;
  attemptCount: number;
  completedAt: string | null;
  errorMessage: string | null;
}

const BASE = '/platform/storage/provider-migrations';

export async function listProviderMigrations(): Promise<ProviderMigrationListItemDto[]> {
  const r = await platformApiClient.get(BASE);
  return Array.isArray(r.data) ? (r.data as ProviderMigrationListItemDto[]) : [];
}

export async function getProviderMigration(id: string): Promise<ProviderMigrationDetailDto | null> {
  const r = await platformApiClient.get(`${BASE}/${id}`);
  return (r.data as ProviderMigrationDetailDto | null) ?? null;
}

export async function listProviderMigrationItems(id: string): Promise<ProviderMigrationItemDto[]> {
  const r = await platformApiClient.get(`${BASE}/${id}/items`);
  return Array.isArray(r.data) ? (r.data as ProviderMigrationItemDto[]) : [];
}

/** What the server returns after creating a plan. */
export interface ProviderMigrationPlanResultDto {
  migrationId: string;
  status: string;
  sourceProviderId: string;
  targetProviderId: string;
  totalObjects: number;
  itemsCreated: number;
  excludedCount: number;
  excludedAssetIds: string[];
  plannedBytes: number;
  largestObjectBytes: number;
  dryRunApproved: false;
  executionImplemented: false;
}

/**
 * MUTATING CALL 1 OF 5.
 *
 * Creates a provider migration PLAN via the already-deployed, already-proven
 * `POST /plan` endpoint (`platform:system:manage`). The server performs its own
 * dry run and derives the ENTIRE scope — eligible assets, exclusions, object
 * count, byte total and the item list — from the database. The two provider ids
 * below are the only input it accepts; the browser cannot influence what is
 * planned.
 *
 * Planning copies nothing, downloads no object content, deletes nothing and
 * changes no `provider_id`. It writes one `storage_migrations` row and its item
 * snapshot, and stops at `DRY_RUN_COMPLETE` awaiting a separate authorization.
 */
export async function planProviderMigration(
  sourceProviderId: string,
  targetProviderId: string,
): Promise<ProviderMigrationPlanResultDto> {
  const r = await platformApiClient.post(`${BASE}/plan`, {
    sourceProviderId,
    targetProviderId,
  });
  return r.data as ProviderMigrationPlanResultDto;
}

/** What the server returns after finalizing. */
export interface ProviderMigrationFinalizeResultDto {
  migrationId: string;
  status: string;
  alreadyCompleted: boolean;
  gatesEvaluated: number;
  gatesPassed: number;
  itemsVerified: number;
  targetObjectsRehashed: number;
  sourceObjectsConfirmedPresent: number;
  protectedAssetsExcluded: number;
  auditRecorded: boolean;
  migrationRowsWritten: 0 | 1;
  objectsDeleted: 0;
  providerConfigChanges: 0;
}

/**
 * MUTATING CALL 5 OF 5.
 *
 * Finalizes a fully verified migration: `VERIFYING -> COMPLETED`
 * (`platform:system:manage`). The server re-proves all twenty completion gates
 * from scratch first — re-hashing every destination object and confirming every
 * SOURCE object is still present — and writes exactly one migration row plus
 * one audit record.
 *
 * It copies nothing, moves nothing, changes no `provider_id`, touches no
 * provider configuration and DELETES NOTHING. Source objects stay retained;
 * their continued presence is itself one of the gates.
 *
 * IRREVERSIBLE THROUGH THE MIGRATION API. `COMPLETED` is terminal, and
 * `/ownership/rollback` requires status `VERIFYING`, so once this succeeds the
 * ownership rollback endpoint can no longer be called for this migration. The
 * caller MUST obtain an explicit human confirmation before invoking it — see
 * `FinalizeMigrationPanel`, which is the only caller.
 */
export async function finalizeProviderMigration(
  id: string,
): Promise<ProviderMigrationFinalizeResultDto> {
  const r = await platformApiClient.post(`${BASE}/${id}/finalize`);
  return r.data as ProviderMigrationFinalizeResultDto;
}

/** What the server returns after approving a completed dry run. */
export interface ProviderMigrationApproveResultDto {
  id: string;
  status: string;
  approved: boolean;
}

/**
 * MUTATING CALL 2 OF 5.
 *
 * Approves a completed dry run: `DRY_RUN_COMPLETE -> READY`
 * (`platform:system:manage`). This is the explicit human gate the frozen
 * document requires between planning and execution.
 *
 * It COPIES NOTHING, touches no object, changes no `provider_id` and deletes
 * nothing — it writes one row. The server refuses any status other than
 * `DRY_RUN_COMPLETE`, so this can never re-approve an already-approved
 * migration.
 */
export async function approveProviderMigration(
  id: string,
): Promise<ProviderMigrationApproveResultDto> {
  const r = await platformApiClient.post(`${BASE}/${id}/approve`);
  return r.data as ProviderMigrationApproveResultDto;
}

/** What the server returns after a copy-and-verify run. */
export interface ProviderMigrationRunResultDto {
  migrationId: string;
  status: string;
  totalObjects: number;
  processed: number;
  copied: number;
  /** Objects already present on the destination and byte-identical: no upload. */
  alreadyPresentAndIdentical: number;
  verified: number;
  failed: number;
  failuresByReason: Record<string, number>;
  bytesCopied: number;
  /** The run never flips ownership. Always 0. */
  providerIdChanges: 0;
  /** The run never deletes. Always 0. */
  sourceObjectsDeleted: 0;
}

/**
 * MUTATING CALL 3 OF 5.
 *
 * Copies and verifies the planned objects (`platform:system:manage`). For each
 * planned item the server reads the source, writes the destination under the
 * same key and re-reads it, completing the item only when the two SHA-256
 * digests match. An object already present on the destination and byte-
 * identical is REUSED without uploading.
 *
 * **It does not change ownership.** `providerIdChanges` is always 0 — the
 * ownership transition is a separate, separately authorized operation with no
 * wrapper in this file. It deletes nothing: `sourceObjectsDeleted` is always 0
 * and source objects are retained.
 *
 * The server refuses any status other than `READY` or `PAUSED`. A successful
 * run leaves the migration at `VERIFYING`; a run with failures leaves it
 * `PAUSED`, which is NOT complete.
 */
/**
 * The run is INLINE on the server — it copies and verifies every object before
 * it responds — so this one request legitimately outlives the client's default
 * 30-second timeout. At the default, the browser ABORTED a perfectly healthy run
 * at 30s while the server carried on, and the page then reported a transport
 * failure for a migration that was progressing normally. That was the single
 * largest cause of the "I have to refresh to find out what happened" problem.
 *
 * Raising the ceiling does NOT make this request the mechanism for observing the
 * run — `useMigrationLive` is. Aborting here cancels nothing server-side, so a
 * timeout must be reported as "still running", never as a failure.
 */
const RUN_REQUEST_TIMEOUT_MS = 15 * 60 * 1000;

export async function runProviderMigration(id: string): Promise<ProviderMigrationRunResultDto> {
  const r = await platformApiClient.post(`${BASE}/${id}/run`, undefined, {
    timeout: RUN_REQUEST_TIMEOUT_MS,
  });
  return r.data as ProviderMigrationRunResultDto;
}

/**
 * True when the failure is a CLIENT-SIDE abort (timeout or dropped connection)
 * rather than a decision by the server.
 *
 * This distinction matters and must never be blurred: the server does not
 * observe the client going away, so an aborted run request means the copy is
 * STILL RUNNING. Reporting it as a failure would be wrong, and offering a retry
 * would invite a second concurrent run.
 */
export function isClientAbort(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const e = err as { code?: unknown; message?: unknown; response?: unknown };
  if (e.response !== undefined && e.response !== null) return false; // the server answered
  const code = typeof e.code === 'string' ? e.code : '';
  const message = typeof e.message === 'string' ? e.message : '';
  return (
    code === 'ECONNABORTED' ||
    code === 'ETIMEDOUT' ||
    code === 'ERR_NETWORK' ||
    /timeout|network error/i.test(message)
  );
}

/** What the server returns from an ownership transition or its rehearsal. */
export interface ProviderMigrationOwnershipResultDto {
  migrationId: string;
  direction: 'FLIP' | 'ROLLBACK';
  /** True when nothing was written — a rehearsal. */
  dryRun: boolean;
  totalItems: number;
  eligible: number;
  /** On a rehearsal this is what WOULD move; on a real flip, what did. */
  flipped: number;
  alreadyInTargetState: number;
  failed: number;
  failuresByReason: Record<string, number>;
  tenantsProcessed: number;
  /** The ownership step never deletes. Always 0. */
  objectsDeleted: 0;
  /** The ownership step never changes provider enablement. Always 0. */
  providerConfigChanges: 0;
  /** The ownership step never writes the migration's status. Always false. */
  migrationStatusChanged: false;
}

/**
 * READ-ONLY REHEARSAL. A POST that writes nothing.
 *
 * Runs every check the real flip runs — including re-downloading each
 * destination object and comparing its SHA-256 against the checksum recorded at
 * copy time — and reports what WOULD move. `dryRun` is true in the response and
 * `flipped` is the would-move count, not a count of changes.
 *
 * Gated on `platform:system:view`. It changes no `provider_id`, writes no row
 * and deletes nothing, so it is safe to run before deciding anything.
 */
export async function previewProviderMigrationOwnership(
  id: string,
): Promise<ProviderMigrationOwnershipResultDto> {
  const r = await platformApiClient.post(`${BASE}/${id}/ownership/preview`);
  return r.data as ProviderMigrationOwnershipResultDto;
}

/**
 * MUTATING CALL 4 OF 5.
 *
 * The ownership transition itself (`platform:system:manage`): moves
 * `storage_assets.provider_id` from the source to the target provider for
 * VERIFIED items only. This is the step that sits between Run and Finalize, and
 * the step whose absence made finalization refuse.
 *
 * The server re-proves each destination object exists and still hashes to the
 * recorded checksum BEFORE moving that asset, writes one asset per tenant-scoped
 * transaction asserting exactly one affected row, and refuses any status other
 * than VERIFYING. It DELETES NOTHING — source objects stay retained —
 * changes no provider configuration, and does not touch the migration's status.
 *
 * REVERSIBLE, for now: `/ownership/rollback` exists on the server and requires
 * status VERIFYING, so the flip can be undone until the migration is finalized.
 * This client deliberately exposes no rollback wrapper; reversal remains an
 * out-of-band, separately authorized operation.
 *
 * The caller MUST obtain an explicit human confirmation before invoking it —
 * see `OwnershipTransferPanel`, which is the only caller.
 */
export async function flipProviderMigrationOwnership(
  id: string,
): Promise<ProviderMigrationOwnershipResultDto> {
  const r = await platformApiClient.post(`${BASE}/${id}/ownership/flip`);
  return r.data as ProviderMigrationOwnershipResultDto;
}

/**
 * READ-ONLY REHEARSAL. A POST that writes nothing.
 *
 * Runs all twenty completion gates — re-hashing every destination object and
 * confirming every source object is still present — and returns the tally
 * without changing the migration's status. Gated on `platform:system:view`.
 *
 * A refusal is thrown, not returned: the server raises the same governed 409
 * naming the failing gate that the real finalization would raise, which is
 * exactly what makes this useful as a rehearsal.
 */
export async function previewFinalizeProviderMigration(
  id: string,
): Promise<ProviderMigrationFinalizeResultDto> {
  const r = await platformApiClient.post(`${BASE}/${id}/finalize/preview`);
  return r.data as ProviderMigrationFinalizeResultDto;
}
